import {
  FlightJourney,
  FlightSegment,
  ReturnFlightJourney,
} from '@shared/v2/services/types/api/parsedResponse';
import { EcommerceFlightItem } from '@shared/v2/utils/dataLayer';

import formatStops from '@/utils/formatStops';

interface EcommerceFlightItemProps {
  travellers: {
    adults: number;
    children: number;
    infants: number;
  };
  packagePrice?: number;
  flightType: string;
  flightOption: FlightJourney | ReturnFlightJourney;
  index: number;
}

const getJoinedFlightNumbers = (flightSegments: FlightSegment[]): string =>
  flightSegments.map(({ flightNumber }) => flightNumber).join('-');

const getJoinedOriginDestinationCodes = (
  flightSegments: FlightSegment[],
): string =>
  flightSegments
    .map(({ origin, destination }) => `${origin.code} - ${destination.code}`)
    .join(',');

const getJoinedFlightClasses = (flightSegments: FlightSegment[]): string =>
  flightSegments.map(({ flightClass }) => flightClass).join('-');

export const hasReturnFlightJourney = (
  flightOption: FlightJourney | ReturnFlightJourney,
): flightOption is ReturnFlightJourney => {
  return (
    'flightSegments' in flightOption &&
    !Array.isArray(flightOption.flightSegments) &&
    'inbound' in flightOption.flightSegments &&
    'outbound' in flightOption.flightSegments
  );
};

export const getEcommerceFlightItem = ({
  travellers,
  packagePrice,
  flightOption,
  flightType,
  index,
}: EcommerceFlightItemProps): EcommerceFlightItem => {
  const numberOfPassengers =
    (travellers.adults ?? 0) +
    (travellers.children ?? 0) +
    (travellers.infants ?? 0);

  const hasReturnFlight = hasReturnFlightJourney(flightOption);

  const segments = hasReturnFlight
    ? flightOption.flightSegments
    : { outbound: flightOption.flightSegments, inbound: [] };

  const outboundFlightNumbers = getJoinedFlightNumbers(segments.outbound);
  const outboundOriginDestination = getJoinedOriginDestinationCodes(
    segments.outbound,
  );
  const outboundFlightClasses = getJoinedFlightClasses(segments.outbound);
  const outboundStops = formatStops(segments.outbound.length);

  const inboundData = hasReturnFlight
    ? {
        flightNumbers: getJoinedFlightNumbers(segments.inbound),
        originDestination: getJoinedOriginDestinationCodes(segments.inbound),
        flightClasses: getJoinedFlightClasses(segments.inbound),
        stops: formatStops(segments.inbound.length),
      }
    : null;

  const formatReturnData = (outbound: string, inbound: string | undefined) =>
    inbound ? `${outbound} / ${inbound}` : outbound;

  const flightNumbers = formatReturnData(
    outboundFlightNumbers,
    inboundData?.flightNumbers,
  );

  const travelClass = formatReturnData(
    outboundFlightClasses,
    inboundData?.flightClasses,
  );

  return {
    index,
    item_id: flightNumbers,
    item_category: 'Flight',
    item_name: formatReturnData(
      outboundOriginDestination,
      inboundData?.originDestination,
    ),
    item_variant: travelClass,
    flight_number: flightNumbers,
    travel_class: travelClass,
    flight_type: flightType,
    stops: formatReturnData(outboundStops, inboundData?.stops),
    international_or_domestic: hasReturnFlight ? 'International' : 'Domestic',
    price: packagePrice,
    quantity: numberOfPassengers,
    travellers_adults: travellers.adults,
    travellers_children: travellers.children,
    travellers_infants: travellers.infants,
  };
};
